// Správa API klíčů pro MCP integraci.
//
// Komunikuje s `/api/api-keys` (admin-only). UI je v sekci #settings-api-keys
// definované v src/index.html. Veškerá business logika (hash, expirace, audit)
// zůstává na backendu – tady řešíme jen formuláře a notifikace.

const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:3001/api'
  : '/api';

// Fallback labely – preferenčně se použijí ty, které vrátí backend
// (GET /api/api-keys → pricingTierLabels), díky čemuž je frontend
// nezávislý na konkrétních hodnotách tieru.
const TIER_LABELS_FALLBACK = {
  velko: 'Velkoobchodní',
  malo: 'Maloobchodní',
};

function notify(message, type = 'success') {
  if (typeof window.showNotification === 'function') {
    window.showNotification(message, type);
  } else {
    console.log(`[${type}] ${message}`);
  }
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('cs-CZ') + ' ' + d.toLocaleTimeString('cs-CZ', { hour: '2-digit', minute: '2-digit' });
}

function statusBadge(key) {
  if (key.revokedAt) {
    return '<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">Odvolaný</span>';
  }
  if (key.expiresAt && new Date(key.expiresAt) < new Date()) {
    return '<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">Propadlý</span>';
  }
  return '<span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Aktivní</span>';
}

export class ApiKeysAdmin {
  constructor() {
    this.listEl = document.getElementById('api-keys-list');
    this.emptyEl = document.getElementById('api-keys-empty');
    this.createBtn = document.getElementById('api-key-create-btn');
    this.createModal = document.getElementById('api-key-create-modal');
    this.editModal = document.getElementById('api-key-edit-modal');
    this.showModal = document.getElementById('api-key-show-modal');
    this.plaintextEl = document.getElementById('api-key-plaintext');
    this.tierLabels = { ...TIER_LABELS_FALLBACK };
    this.keys = [];
    this.loaded = false;

    if (!this.listEl || !this.createBtn) return;

    this.bindEvents();
  }

  bindEvents() {
    this.createBtn.addEventListener('click', () => this.openCreateModal());

    document.getElementById('api-key-create-cancel').addEventListener('click', () => this.closeCreateModal());
    document.getElementById('api-key-create-submit').addEventListener('click', () => this.submitCreate());
    document.getElementById('api-key-edit-cancel').addEventListener('click', () => this.closeEditModal());
    document.getElementById('api-key-edit-submit').addEventListener('click', () => this.submitEdit());
    document.getElementById('api-key-show-close').addEventListener('click', () => this.closeShowModal());
    document.getElementById('api-key-copy-btn').addEventListener('click', () => this.copyPlaintext());

    // Klik mimo modal = zavřít.
    this.createModal.addEventListener('click', (e) => { if (e.target === this.createModal) this.closeCreateModal(); });
    this.editModal.addEventListener('click', (e) => { if (e.target === this.editModal) this.closeEditModal(); });
    this.showModal.addEventListener('click', (e) => { if (e.target === this.showModal) this.closeShowModal(); });

    // Event delegation pro tlačítka edit/revoke/delete v seznamu.
    this.listEl.addEventListener('click', (e) => {
      const editBtn = e.target.closest('[data-action="edit"]');
      if (editBtn) {
        const id = editBtn.getAttribute('data-id');
        this.openEditModal(id);
        return;
      }
      const revokeBtn = e.target.closest('[data-action="revoke"]');
      if (revokeBtn) {
        const id = revokeBtn.getAttribute('data-id');
        const name = revokeBtn.getAttribute('data-name');
        if (confirm(`Opravdu zneplatnit klíč "${name}"? Klient s tímto klíčem ztratí přístup.`)) {
          this.revoke(id);
        }
        return;
      }
      const deleteBtn = e.target.closest('[data-action="delete"]');
      if (deleteBtn) {
        const id = deleteBtn.getAttribute('data-id');
        const name = deleteBtn.getAttribute('data-name');
        if (confirm(`Trvale smazat klíč "${name}" z evidence?`)) {
          this.delete(id);
        }
      }
    });
  }

  async fetchKeys() {
    const res = await fetch(`${API_BASE_URL}/api-keys`, { credentials: 'include' });
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Neznámá chyba');
    if (data.pricingTierLabels && typeof data.pricingTierLabels === 'object') {
      this.tierLabels = { ...TIER_LABELS_FALLBACK, ...data.pricingTierLabels };
    }
    return data.data || [];
  }

  async load() {
    try {
      const keys = await this.fetchKeys();
      this.keys = keys;
      this.loaded = true;
      this.render(keys);
    } catch (err) {
      console.error('Chyba při načítání API klíčů:', err);
      this.listEl.innerHTML = `<div class="p-6 text-sm text-red-600 text-center">Chyba: ${err.message}</div>`;
    }
  }

  tierLabel(tier) {
    return this.tierLabels[tier] || tier || '—';
  }

  render(keys) {
    if (!keys.length) {
      this.listEl.innerHTML = `
        <div class="p-6 text-sm text-gray-500 text-center">
          Zatím žádné API klíče. Vytvoř první klikem na <strong>Vytvořit klíč</strong>.
        </div>`;
      return;
    }

    const rows = keys.map((k) => {
      const tier = this.tierLabel(k.pricingTier);
      const tierBadgeClass = k.pricingTier === 'malo'
        ? 'bg-blue-100 text-blue-800'
        : 'bg-purple-100 text-purple-800';
      const isActive = !k.revokedAt && (!k.expiresAt || new Date(k.expiresAt) >= new Date());
      return `
        <tr class="border-t border-gray-100">
          <td class="px-4 py-3 text-sm">
            <div class="font-medium text-ink">${escapeHtml(k.name)}</div>
            <div class="text-xs text-gray-500 font-mono">tria_…${escapeHtml(k.lastFour || '????')}</div>
          </td>
          <td class="px-4 py-3 text-sm">
            <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${tierBadgeClass}">${escapeHtml(tier)}</span>
          </td>
          <td class="px-4 py-3 text-sm">${statusBadge(k)}</td>
          <td class="px-4 py-3 text-xs text-gray-600">
            <div>Vytvořeno: ${formatDate(k.createdAt)}</div>
            ${k.expiresAt ? `<div>Platnost: ${formatDate(k.expiresAt)}</div>` : ''}
            ${k.lastUsedAt ? `<div>Posl. použití: ${formatDate(k.lastUsedAt)} (${k.usageCount || 0}×)</div>` : '<div class="text-gray-400">Zatím nepoužito</div>'}
          </td>
          <td class="px-4 py-3 text-sm text-right whitespace-nowrap">
            ${isActive ? `<button data-action="edit" data-id="${k.id}" class="btn btn-ghost btn-sm text-blue-700 hover:bg-blue-50">Upravit</button>` : ''}
            ${isActive ? `<button data-action="revoke" data-id="${k.id}" data-name="${escapeHtml(k.name)}" class="btn btn-ghost btn-sm text-yellow-700 hover:bg-yellow-50 ml-1">Zneplatnit</button>` : ''}
            <button data-action="delete" data-id="${k.id}" data-name="${escapeHtml(k.name)}" class="btn btn-ghost btn-sm text-red-700 hover:bg-red-50 ml-1">Smazat</button>
          </td>
        </tr>`;
    }).join('');

    this.listEl.innerHTML = `
      <table class="w-full">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Název / klíč</th>
            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tier</th>
            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stav</th>
            <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Detaily</th>
            <th class="px-4 py-2"></th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>`;
  }

  openCreateModal() {
    document.getElementById('api-key-name').value = '';
    document.getElementById('api-key-tier').value = 'velko';
    document.getElementById('api-key-expires').value = '';
    this.createModal.classList.remove('hidden');
    setTimeout(() => document.getElementById('api-key-name').focus(), 50);
  }

  closeCreateModal() {
    this.createModal.classList.add('hidden');
  }

  openEditModal(id) {
    const key = (this.keys || []).find((k) => k.id === id);
    if (!key) {
      notify('Klíč nenalezen, obnov stránku', 'warning');
      return;
    }
    document.getElementById('api-key-edit-id').value = key.id;
    document.getElementById('api-key-edit-name').value = key.name || '';
    document.getElementById('api-key-edit-tier').value = key.pricingTier || 'velko';
    document.getElementById('api-key-edit-expires').value = key.expiresAt
      ? new Date(key.expiresAt).toISOString().slice(0, 10)
      : '';
    this.editModal.classList.remove('hidden');
    setTimeout(() => document.getElementById('api-key-edit-name').focus(), 50);
  }

  closeEditModal() {
    this.editModal.classList.add('hidden');
  }

  async submitEdit() {
    const id = document.getElementById('api-key-edit-id').value;
    const name = document.getElementById('api-key-edit-name').value.trim();
    const pricingTier = document.getElementById('api-key-edit-tier').value;
    const expiresInput = document.getElementById('api-key-edit-expires').value;
    if (!id) return;

    try {
      const body = { name, pricingTier };
      body.expiresAt = expiresInput
        ? new Date(expiresInput + 'T23:59:59').toISOString()
        : null;
      const res = await fetch(`${API_BASE_URL}/api-keys/${id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      this.closeEditModal();
      notify('Klíč upraven', 'success');
      this.load();
    } catch (err) {
      console.error('Chyba při úpravě klíče:', err);
      notify(`Chyba: ${err.message}`, 'error');
    }
  }

  async submitCreate() {
    const name = document.getElementById('api-key-name').value.trim();
    const pricingTier = document.getElementById('api-key-tier').value;
    const expiresInput = document.getElementById('api-key-expires').value;

    if (!name) {
      notify('Zadej název klíče', 'warning');
      return;
    }

    try {
      const body = { name, pricingTier };
      if (expiresInput) {
        body.expiresAt = new Date(expiresInput + 'T23:59:59').toISOString();
      }
      const res = await fetch(`${API_BASE_URL}/api-keys`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      this.closeCreateModal();
      this.showNewKey(data.data.plaintextKey);
      this.load();
      notify('API klíč vytvořen', 'success');
    } catch (err) {
      console.error('Chyba při vytváření klíče:', err);
      notify(`Chyba: ${err.message}`, 'error');
    }
  }

  showNewKey(plaintext) {
    this.plaintextEl.textContent = plaintext;
    this.showModal.classList.remove('hidden');
  }

  closeShowModal() {
    this.showModal.classList.add('hidden');
    this.plaintextEl.textContent = '';
  }

  async copyPlaintext() {
    try {
      await navigator.clipboard.writeText(this.plaintextEl.textContent);
      notify('Klíč zkopírován do schránky', 'success');
    } catch (err) {
      notify('Nepodařilo se zkopírovat – označ a zkopíruj ručně.', 'warning');
    }
  }

  async revoke(id) {
    try {
      const res = await fetch(`${API_BASE_URL}/api-keys/${id}/revoke`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      notify('Klíč zneplatněn', 'success');
      this.load();
    } catch (err) {
      console.error('Chyba při zneplatnění klíče:', err);
      notify(`Chyba: ${err.message}`, 'error');
    }
  }

  async delete(id) {
    try {
      const res = await fetch(`${API_BASE_URL}/api-keys/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || `HTTP ${res.status}`);
      notify('Klíč smazán', 'success');
      this.load();
    } catch (err) {
      console.error('Chyba při mazání klíče:', err);
      notify(`Chyba: ${err.message}`, 'error');
    }
  }
}

function escapeHtml(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
